import classNames from 'classnames';
import type { ComponentProps, ReactNode, Ref } from 'react';
import type { To } from 'react-router';
import styled from 'styled-components';

import { NavLink } from '@sorare/routing';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconLock } from '#src/atoms/icons/system/iconLock.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { Badge } from '#src/atoms/ui/Badge/index.tsx';
import { LiveDot } from '#src/components/live/LiveDot/index.tsx';
import {
  type FlyTargetKey,
  useRegisterFlyTarget,
} from '#src/contexts/flyTarget/index.tsx';
import {
  useContextNavigation,
  useStoredContextNavigation,
} from '#src/hooks/useContextNavigation.ts';
import { breakpoints, laptopAndAbove } from '#src/style/mediaQuery.ts';

const Root = styled(Horizontal)`
  justify-content: center;
  width: 100%;
  cursor: pointer;
  color: var(--c-onBase-secondary);

  &.locked {
    cursor: default;
    pointer-events: none;
  }

  &.horizontal {
    justify-content: flex-start;
    padding: var(--intermediate-unit);
    border-radius: var(--unit);

    &:not(.unactive) {
      &&.active {
        background-color: var(--c-surface-primary);
      }
    }
    &:hover {
      background-color: rgba(var(--c-rgb-white), 0.04);
      color: var(--c-onBase-primary);
    }
    &.sidebarCollapsed {
      max-width: calc(5 * var(--unit));
      overflow: hidden;
    }
    &:not(.active).sidebarCollapsed:hover {
      transform: scale(1.2);
    }
    transition: 350ms transform var(--ease-out-slide);
  }
  &:not(.unactive) {
    &.active {
      color: var(--c-onBase-primary);
    }
  }
`;
const Layout = styled(Vertical)`
  position: relative;
  align-items: center;
  gap: var(--half-unit);
  justify-content: flex-start;
  width: 100%;

  .horizontal & {
    flex-direction: row;
    gap: var(--intermediate-unit);
  }
  @media ${laptopAndAbove} {
    ${Root}:not(.active, .sidebarCollapsed):hover > & {
      transform: translateX(var(--half-unit));
    }
  }
  transition: 350ms transform var(--ease-out-slide);
`;
const RelativeWrapper = styled(Horizontal)`
  position: relative;
`;
const IconWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
  min-width: var(--double-unit);
  min-height: var(--double-unit);

  .active.horizontal & {
    transform: scale(1.2);
  }
`;
const LockBadge = styled.div`
  position: absolute;
  top: calc(-1 * var(--half-unit));
  right: calc(-1 * var(--half-unit));
  color: var(--c-onBase-primary);
`;
const FullWidthSBHorizontal = styled(Horizontal)`
  width: 100%;
  flex: 1;
  justify-content: center;

  .horizontal & {
    justify-content: space-between;
    white-space: nowrap;
  }
`;
const PlayLiveDotWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  right: calc(-1 * var(--double-unit));
`;
const StyledBadge = styled(Badge)`
  margin-right: 0;
  .horizontal & {
    margin-right: var(--unit);
  }
  .locked & {
    opacity: 0.2;
  }
`;
const NavLabel = styled.span`
  font: var(--t-label-xs);
  white-space: nowrap;
  @media (min-width: ${breakpoints.se}px) {
    font: var(--t-label-s);
  }
  .horizontal & {
    font: var(--t-label-l);
  }
  .locked & {
    opacity: 0.2;
  }
`;

export const Separator = styled.div`
  width: 100%;
  height: 0px;
  border-top: 1px solid var(--c-ui-primary);
`;

type Props = {
  to?: To;
  icon?: ({ isActive }: { isActive: boolean }) => ReactNode;
  label: string;
  /** Rendered in normal flow after the label, unlike the absolutely-positioned `badge`. */
  labelSuffix?: ReactNode;
  controlledActive?: boolean;
  showLiveDot?: boolean;
  badge?: { content: ReactNode; ariaLabel?: string };
  variant?: 'horizontal' | 'vertical';
  sidebarCollapsed?: boolean;
  onClick?: () => void;
  keepSearchParams?: boolean;
  matches?: ComponentProps<typeof NavLink>['matches'];
  enableContextModeNavigation?: boolean;
  enabledStoredLastNavigation?: boolean;
  end?: ComponentProps<typeof NavLink>['end'];
  flyTargetKey?: FlyTargetKey;
  locked?: boolean;
};

const NavLinkContent = ({
  sidebarCollapsed,
  variant,
  label,
  labelSuffix,
  badge,
  icon,
  isActive,
  showLiveDot,
  iconRef,
  locked,
}: Pick<
  Props,
  | 'sidebarCollapsed'
  | 'showLiveDot'
  | 'variant'
  | 'label'
  | 'labelSuffix'
  | 'badge'
  | 'icon'
  | 'locked'
> & { isActive?: boolean; iconRef?: Ref<HTMLDivElement> }) => {
  const isDesktopLayout = variant === 'horizontal';
  return (
    <Layout>
      <Tooltip
        skip={!sidebarCollapsed || variant === 'vertical'}
        title={<LabelM>{label}</LabelM>}
        placement="right"
      >
        <IconWrapper ref={iconRef}>
          <StyledBadge
            invisible={
              !badge || (isDesktopLayout && !sidebarCollapsed) || locked
            }
            badgeContent={
              badge && (!isDesktopLayout || sidebarCollapsed)
                ? badge?.content
                : undefined
            }
            aria-label={
              badge && (!isDesktopLayout || sidebarCollapsed)
                ? badge?.ariaLabel
                : undefined
            }
          >
            {icon?.({ isActive: isActive ?? false })}
          </StyledBadge>

          {!!locked && (
            <LockBadge>
              <FontAwesomeIcon icon={iconLock} size="xs" />
            </LockBadge>
          )}
        </IconWrapper>
      </Tooltip>
      <FullWidthSBHorizontal>
        <RelativeWrapper>
          <NavLabel className="text-center">{label}</NavLabel>
          {!!showLiveDot && (
            <PlayLiveDotWrapper>
              <LiveDot animate size="md" />
            </PlayLiveDotWrapper>
          )}
        </RelativeWrapper>
        {labelSuffix}
        {!!(badge && isDesktopLayout) && (
          <StyledBadge
            invisible={locked}
            badgeContent={badge?.content}
            aria-label={badge?.ariaLabel}
          />
        )}
      </FullWidthSBHorizontal>
    </Layout>
  );
};

export const NavItem = ({
  to,
  icon,
  label,
  labelSuffix,
  controlledActive,
  showLiveDot,
  badge,
  variant = 'horizontal',
  sidebarCollapsed,
  enableContextModeNavigation = false,
  enabledStoredLastNavigation = false,
  flyTargetKey,
  locked = false,
  ...rest
}: Props) => {
  const storedLastNav = useStoredContextNavigation({
    to: enabledStoredLastNavigation ? to : undefined,
  });
  const savedTo = useContextNavigation({
    to: enableContextModeNavigation ? to : undefined,
  });
  const computedTo = locked ? undefined : savedTo || storedLastNav || to;
  const flyTargetRef = useRegisterFlyTarget<HTMLDivElement>(flyTargetKey);

  if (computedTo) {
    return (
      <Root
        as={NavLink}
        to={computedTo}
        state={{
          fromNavItem: !(savedTo || storedLastNav),
        }}
        className={classNames({
          horizontal: variant === 'horizontal',
          active: controlledActive,
          unactive: controlledActive === false,
          sidebarCollapsed,
        })}
        {...rest}
      >
        {({ isActive }) => (
          <NavLinkContent
            sidebarCollapsed={sidebarCollapsed}
            variant={variant}
            label={label}
            labelSuffix={labelSuffix}
            badge={badge}
            icon={icon}
            isActive={
              controlledActive === undefined ? isActive : controlledActive
            }
            showLiveDot={showLiveDot}
            iconRef={flyTargetRef}
          />
        )}
      </Root>
    );
  }

  return (
    <Root
      className={classNames({
        horizontal: variant === 'horizontal',
        active: controlledActive,
        unactive: controlledActive === false,
        sidebarCollapsed,
        locked,
      })}
      {...rest}
    >
      <NavLinkContent
        sidebarCollapsed={sidebarCollapsed}
        variant={variant}
        label={label}
        labelSuffix={labelSuffix}
        badge={badge}
        icon={icon}
        showLiveDot={showLiveDot}
        iconRef={flyTargetRef}
        locked={locked}
      />
    </Root>
  );
};
